"use client"

const AppCard = ({ icon, iconClass, name, description, appName, onOpenApp, color }) => {
  return (
    <div 
      className={`${iconClass} p-6 rounded-2xl glass-effect cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-xl group border border-white/10`}
      onClick={() => onOpenApp(appName)}
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div className="p-4 rounded-xl bg-black/20 group-hover:bg-black/30 transition-colors">
          <i className={`${icon} ${color} text-4xl`}></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
            {name}
          </h3>
          <p className="text-sm text-gray-300 group-hover:text-gray-200 transition-colors">
            {description}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function AppsApp({ onOpenApp }) {
  const applications = [
    { 
      icon: 'fas fa-calculator', 
      iconClass: 'bg-gradient-to-br from-blue-500/20 to-cyan-500/20', 
      name: 'Calculator', 
      description: 'Basic calculations',
      appName: 'calculator', 
      color: 'text-blue-400' 
    },
    { 
      icon: 'fas fa-sticky-note', 
      iconClass: 'bg-gradient-to-br from-yellow-500/20 to-orange-500/20', 
      name: 'Notepad', 
      description: 'Text editor',
      appName: 'notepad', 
      color: 'text-yellow-400' 
    },
    { 
      icon: 'fas fa-calendar', 
      iconClass: 'bg-gradient-to-br from-red-500/20 to-rose-500/20', 
      name: 'Calendar', 
      description: 'Schedule events',
      appName: 'calendar', 
      color: 'text-red-400' 
    },
    { 
      icon: 'fab fa-youtube', 
      iconClass: 'bg-gradient-to-br from-red-600/20 to-red-500/20', 
      name: 'YouTube', 
      description: 'Video streaming',
      appName: 'youtube', 
      color: 'text-red-500' 
    },
    { 
      icon: 'fab fa-spotify', 
      iconClass: 'bg-gradient-to-br from-green-500/20 to-emerald-600/20', 
      name: 'Spotify', 
      description: 'Music streaming',
      appName: 'spotify', 
      color: 'text-green-500' 
    },
    { 
      icon: 'fas fa-envelope', 
      iconClass: 'bg-gradient-to-br from-indigo-500/20 to-purple-500/20', 
      name: 'Mail', 
      description: 'Email client',
      appName: 'mail', 
      color: 'text-indigo-400' 
    },
    { 
      icon: 'fas fa-cloud-sun', 
      iconClass: 'bg-gradient-to-br from-sky-500/20 to-blue-500/20', 
      name: 'Weather', 
      description: 'Weather forecast',
      appName: 'weather', 
      color: 'text-sky-400' 
    },
    { 
      icon: 'fas fa-cog', 
      iconClass: 'bg-gradient-to-br from-gray-500/20 to-slate-500/20', 
      name: 'Settings', 
      description: 'System preferences',
      appName: 'settings', 
      color: 'text-gray-400' 
    },
    { 
      icon: 'fas fa-palette', 
      iconClass: 'bg-gradient-to-br from-pink-500/20 to-purple-500/20', 
      name: 'Color Picker', 
      description: 'Advanced color tool',
      appName: 'colorpicker', 
      color: 'text-pink-400' 
    },
    { 
      icon: 'fas fa-newspaper', 
      iconClass: 'bg-gradient-to-br from-orange-500/20 to-red-500/20', 
      name: 'Developer News', 
      description: 'Latest tech news',
      appName: 'news', 
      color: 'text-orange-400' 
    },
    { 
      icon: 'fas fa-terminal', 
      iconClass: 'bg-gradient-to-br from-green-500/20 to-teal-500/20', 
      name: 'Terminal', 
      description: 'Command line interface',
      appName: 'terminal', 
      color: 'text-green-400' 
    }
  ]

  const handleOpenApp = (appName) => {
    // If onOpenApp is passed from parent, use it (for when launched from desktop)
    if (onOpenApp) {
      onOpenApp(appName)
    } else {
      // For now, we'll just log - this would need to be connected to the main app
      console.log(`Opening ${appName}`)
    }
  }

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-gray-900 via-blue-900/20 to-purple-900/20 text-white overflow-hidden">
      {/* Header */}
      <div className="bg-black/30 p-6 border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-400/30">
            <i className="fas fa-th text-blue-400 text-3xl"></i>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">
              Applications
            </h1>
            <p className="text-gray-300 text-sm">
              Your productivity suite • {applications.length} apps available
            </p>
          </div>
        </div>
      </div>

      {/* Apps Grid */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {applications.map((app, index) => (
            <AppCard 
              key={index}
              icon={app.icon}
              iconClass={app.iconClass}
              name={app.name}
              description={app.description}
              appName={app.appName}
              onOpenApp={handleOpenApp}
              color={app.color}
            />
          ))}
        </div>

        {/* Coming Soon Section */}
        <div className="mt-12 text-center">
          <h2 className="text-xl font-bold text-gray-400 mb-4">Coming Soon</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-600/30 cursor-not-allowed opacity-60">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-4 rounded-xl bg-black/20">
                  <i className="fas fa-code text-green-400 text-4xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-400">Code Editor</h3>
                  <p className="text-sm text-gray-500">IDE for development</p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-600/30 cursor-not-allowed opacity-60">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-4 rounded-xl bg-black/20">
                  <i className="fas fa-paint-brush text-pink-400 text-4xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-400">Paint</h3>
                  <p className="text-sm text-gray-500">Digital drawing app</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800/30 p-6 rounded-2xl border border-gray-600/30 cursor-not-allowed opacity-60">
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-4 rounded-xl bg-black/20">
                  <i className="fas fa-video text-purple-400 text-4xl"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-400">Video Player</h3>
                  <p className="text-sm text-gray-500">Media playback</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mt-12 bg-black/20 rounded-2xl p-6 border border-white/10">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <i className="fas fa-chart-bar text-blue-400"></i>
            App Statistics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-green-400">{applications.length}</div>
              <div className="text-sm text-gray-300">Apps Available</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-purple-400">3</div>
              <div className="text-sm text-gray-300">Coming Soon</div>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-xl">
              <div className="text-2xl font-bold text-blue-400">∞</div>
              <div className="text-sm text-gray-300">Productivity</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
